import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faCircleNotch,
  faChartLine,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

export default function Gauge({
  currentValue,
  rangeStart,
  rangeEnd,
  useLowSetPoint,
  lowSetPoint,
  useHighSetPoint,
  highSetPoint,
  displayDescription,
  description,
  sensorID,
  updateSelectedForLogs,
}) {
  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  };

  const makeArc = (cx, cy, r, startAngle, endAngle) => {
    // angles go clockwise: 180° (left)  → 0° (right)
    const start = polarToCartesian(cx, cy, r, startAngle);
    const end = polarToCartesian(cx, cy, r, endAngle);
    return `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`;
  };

  // value → angle (180° … 0°)
  const toAngle = (val, min, max) => 180 - ((val - min) / (max - min)) * 180;
  const centerX = 100,
    centerY = 100,
    arcRadius = 90;
  const buffer = 5; // ±5 PSI = yellow zone width
  const green = "rgb(0,186,0)";
  const segments = [];

  // --- build the colour bands, left → right --------------------------------
  let cursor = 180; // start of gauge (left end)

  if (useLowSetPoint) {
    // red |yellow
    const lowRedEnd = toAngle(lowSetPoint, rangeStart, rangeEnd);
    const lowYelEnd = toAngle(lowSetPoint + buffer, rangeStart, rangeEnd);
    segments.push({ a1: cursor, a2: lowRedEnd, color: "red" });
    segments.push({ a1: lowRedEnd, a2: lowYelEnd, color: "yellow" });
    cursor = lowYelEnd;
  }

  // main green section
  const greenEnd = useHighSetPoint
    ? toAngle(highSetPoint - buffer, rangeStart, rangeEnd)
    : 0;
  segments.push({ a1: cursor, a2: greenEnd, color: green });
  cursor = greenEnd;

  if (useHighSetPoint) {
    // yellow | red
    const hiYelEnd = toAngle(highSetPoint, rangeStart, rangeEnd);
    segments.push({ a1: cursor, a2: hiYelEnd, color: "yellow" });
    segments.push({ a1: hiYelEnd, a2: 0, color: "red" });
  }

  // --- needle position ------------------------------------------------------
  const clamped = Math.max(rangeStart, Math.min(currentValue, rangeEnd));
  const needleAng = toAngle(clamped, rangeStart, rangeEnd);
  const innerR = arcRadius - 20;

  const needle = {
    x1: centerX + innerR * Math.cos((needleAng * Math.PI) / 180),
    y1: centerY - innerR * Math.sin((needleAng * Math.PI) / 180),
    x2: centerX + arcRadius * Math.cos((needleAng * Math.PI) / 180),
    y2: centerY - arcRadius * Math.sin((needleAng * Math.PI) / 180),
  };

  let valueColor = green;

  if (
    (useLowSetPoint && currentValue <= lowSetPoint) ||
    (useHighSetPoint && currentValue >= highSetPoint)
  ) {
    valueColor = "red";
  } else if (
    (useLowSetPoint && currentValue <= lowSetPoint + 5) ||
    (useHighSetPoint && currentValue >= highSetPoint - 5)
  ) {
    valueColor = "yellow";
  }

  // pick a radius a little *inside* the coloured ring
  const labelRadius = arcRadius - 10; // 10-px inset

  // positions for “min” (180°) and “max” (0°)
  const left = polarToCartesian(centerX, centerY, labelRadius, 180);
  const right = polarToCartesian(centerX, centerY, labelRadius, 0);

  // --- render ---------------------------------------------------------------
  return (
    <div className="gaugeContainer">
      {displayDescription && (
        <div className="gaugeDescription">{description}</div>
      )}
      <svg viewBox="0 0 200 100" width="200" height="100">
        {segments.map(({ a1, a2, color }, i) => (
          <path
            key={i}
            d={makeArc(centerX, centerY, arcRadius, a1, a2)}
            stroke={color}
            strokeWidth="14"
            fill="none"
          />
        ))}

        <line
          x1={needle.x1}
          y1={needle.y1}
          x2={needle.x2}
          y2={needle.y2}
          stroke="white"
          strokeWidth="6"
        />

        <text
          x={centerX}
          y={60}
          fontSize="25"
          textAnchor="middle"
          fill={valueColor}
          fontWeight="bold"
        >
          {clamped}
        </text>
        <text
          x={centerX}
          y={80}
          fontSize="15"
          textAnchor="middle"
          fill={valueColor}
        >
          PSI
        </text>
        <text
          x={left.x}
          y={left.y}
          fontSize="13"
          textAnchor="start"
          fill="#aaaaaa"
        >
          {rangeStart}
        </text>
        <text
          x={right.x}
          y={right.y}
          fontSize="13"
          textAnchor="end"
          fill="#aaaaaa"
        >
          {rangeEnd}
        </text>
      </svg>
      <FontAwesomeIcon
        className="gaugeLogsButton"
        icon={faChartLine}
        onClick={() =>
          updateSelectedForLogs({
            id: sensorID,
            type: "pressure",
            name: description,
          })
        }
      />
    </div>
  );
}
